import React, { useEffect, useState } from "react";
import { styled } from "@mui/system";
import { v4 as uuidv4 } from "uuid";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox";
import MenuComponent from "./MenuComponent";
import AlertDelete from "./AlertsDialogs/AlertDelete";
import FileViewer from "./FileViewer/FileViewer";
import { enqueueSnackbar } from "notistack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Schedule from "./schedule/Schedule";
import {
  List,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import Folder from "./Folder";
import ClassOfService from "./ClassOfServices/ClassOfService";
import optionsData from "./options.json";
import { useChangeHospitalJSON } from "./useChangeHospitalJSON";
import SearchIcon from "@mui/icons-material/Search";
import MDButton from "components/MDButton";
import { useDeleteNodeMutation } from "features/hospitalSlice";

import { application } from "globals/endpoints";
import { useDeleteInboundCosMutation } from "features/hospitalSlice";
import { useDeleteOutboundCosMutation } from "features/hospitalSlice";

const FileExplorerWrapper = styled("div")({
  display: "flex",
  alignItems: "stretch",
  color: "white",
  height: "100%",
});

const FileNavigator = styled("div")(({ theme }) => ({
  flex: "flex",
  minWidth: "10em",
  // marginRight: "0.5em",
  padding: 0,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const ResizableSidebar = styled("div")({
  width: "333px",
  height: "100%",
  overflowX: "hidden",
  position: "relative",
});

const Resizer = styled("div")({
  width: "5px",
  top: 0,
  right: 0,
  cursor: "col-resize",
  height: "95%",
  position: "absolute",
  backgroundColor: "#7d7d7d",
});

const FileViewerStyle = styled("div")({
  marginLeft: "1em",
  flex: "1 1 auto",
  padding: (theme) => theme.spacing(2),
});

// const featureList = [
//   {
//     icon: <MarkunreadMailboxIcon />,
//     text: "Code Blue",
//   },
//   {
//     icon: <MarkunreadMailboxIcon />,
//     text: "IVR",
//   },
//   {
//     icon: <MarkunreadMailboxIcon />,
//     text: "Call Center",
//   },
// ];

const FileExplorer = ({ entityName, folders, setFolders }) => {
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode } = controller;
  const [width, setWidth] = useState(333);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [isHovering, setisHovering] = useState(false);
  const [splitterPosition, setSplitterPosition] = useState(20);
  const [anchorEl, setAnchorEl] = useState(null);
  const [folderPath, setFolderPath] = useState(null);
  const [isDelete, setIsDelete] = React.useState(false);
  const [currentPage, setCurrentPage] = useState("Home");
  const [path, setPath] = useState([]);
  const [featureListSelected, setFeatureListSelected] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [featureList, setFeatureList] = useState(optionsData?.features || []);
  const { changeHospitalJSON } = useChangeHospitalJSON();
  const [deleteNode] = useDeleteNodeMutation();
  const [deleteInboundCos] = useDeleteInboundCosMutation();
  const [deleteOutboundCos] = useDeleteOutboundCosMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleFolderEdit = (folderId, newName, newNumber, foldersToUpdate) => {
    return foldersToUpdate.map((folder) => {
      if (folder.id === folderId) {
        return { ...folder, name: newName, number: newNumber };
      }
      if (folder.children && folder.children.length > 0) {
        return {
          ...folder,
          children: handleFolderEdit(
            folderId,
            newName,
            newNumber,
            folder.children
          ),
        };
      }
      return folder;
    });
  };

  const handleFolderEditAndSave = (folderId, newName, newNumber) => {
    const updatedFolder = handleFolderEdit(
      folderId,
      newName,
      newNumber,
      folders
    );
    if (updatedFolder) {
      setFolders(updatedFolder);
      changeHospitalJSON(updatedFolder, "updated");
    }
  };

  const handleFolderDelete = (folder) => {
    const folderId = folder?.id;
    const parentId = folder?.parentId;

    setFolders((prevFolders) => {
      const newFolders = [...prevFolders];

      const deleteFolder = (foldersList) => {
        for (let i = 0; i < foldersList.length; i++) {
          if (foldersList[i].id === folderId) {
            if (foldersList[i].children?.length > 0) {
              enqueueSnackbar(
                `You cannot delete ${foldersList[i]?.name} because it has children`,
                {
                  variant: "error",
                  anchorOrigin: { vertical: "top", horizontal: "right" },
                }
              );
              return false;
            }

            // COS delete logic
            if (folder.type === "inbound")
              deleteInboundCos({ id: [folder?.COSid] });
            else if (folder.type === "outbound")
              deleteOutboundCos({ id: [folder?.COSid] });
            else deleteNode({ stringId: [folder?.name] });

            foldersList.splice(i, 1);
            changeHospitalJSON(newFolders, "deleted");
            enqueueSnackbar(`${folder.name} deleted`, {
              variant: "success",
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
            return true;
          }

          if (foldersList[i].children && deleteFolder(foldersList[i].children))
            return true;
        }
        return false;
      };

      deleteFolder(newFolders);
      return newFolders;
    });

    setIsDelete(false);
  };

  const findFolderById = (folders, id) => {
    for (let folder of folders) {
      if (folder.id === id) {
        return folder;
      }
      if (folder?.children?.length > 0) {
        const result = findFolderById(folder.children, id);
        if (result) return result;
      }
    }
    return null;
  };

  const handleMouseDown = (e) => {
    const initialX = e.clientX;

    const handleMouseMove = (event) => {
      const dx = event.clientX - initialX;
      const newWidth = width + dx;
      if (newWidth >= 100) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleConfirmDelete = () => {
    setIsDelete(true);
  };
  const handleCurrentPage = (e, type) => {
    // alert("How Are yuou?")
     e.stopPropagation();
    setCurrentPage(type);
    // setAnchorContextMenu(null);
  };

  // const handleAddParent = (event) => {
  //   setisHovering(true);
  //   setAnchorEl(event.currentTarget);
  // };

  // const handleAddBuilding = () => {
  //   handleCloseMenu();
  //   handleAddFolderAtParent("building");
  // };

  // const handleAddFloor = () => {
  //   handleCloseMenu();
  //   handleAddFolderAtParent("floor");
  // };

  // const handleAddRoom = () => {
  //   handleCloseMenu();
  //   handleAddFolderAtParent("room");
  // };

  const handleSelected = (e, folder) => {
    setCurrentFolder(folder);
    // Open Class of Services form if clicked node is services, inbound, or outbound
    if (
      folder?.type === "services" ||
      folder?.type === "inbound" ||
      folder?.type === "outbound"
    ) {
      setCurrentPage("services");
    }
  };

  const getChildren = (currentFolder) => {
    const res =
      currentFolder?.children?.length > 0
        ? currentFolder?.children
        : currentFolder;
    return res;
  };

  useEffect(() => {
    const getPath = async (folder, currentPath) => {
      if (!folder) return; // Folder not found, exit recursion

      currentPath.unshift(folder.name); // Add current folder to the beginning of the path

      if (folder.parentFolderId) {
        // If the current folder has a parent folder, recursively get its path
        await getPath(folder.parentFolderId, currentPath);
      } else {
        // If no parent folder, we've reached the root folder (Home)
        setPath(currentPath);
      }
    };

    getPath(currentFolder, []);
  }, [currentFolder]);

  useEffect(() => {
    if (
      currentFolder?.type === "inbound services" ||
      currentFolder?.type === "outbound services" ||
      currentFolder?.type === "inbound" ||
      currentFolder?.type === "outbound" ||
      currentFolder?.type === "Home" ||
      currentFolder?.type === "Calendar"
    ) {
      setCurrentPage(currentFolder?.type);
    }
  }, [currentFolder]);


  const handleFolderAdd = (parentId, type, roomNumber) => {
    const newFolder = {
      id: uuidv4(),
      name:
        roomNumber ||
        (type === "building"
          ? "New Building"
          : type === "floor"
          ? "New Floor"
          : type === "features"
          ? "Features"
          : roomNumber),
      // number: (roomNumber?.match(/\d+$/) || [])[0] || "",
      type: type,
      parentId: parentId,
      scheduleIds: [...currentFolder?.scheduleIds] || [],
      children: [],
    };

    const featureId = uuidv4();

    const newFeature = {
      id: featureId,
      name: "Features",
      type: type,
      parentId: parentId,
      scheduleIds: [...currentFolder?.scheduleIds] || [],
      children: featureList
        ?.filter((child) => featureListSelected?.[child.name])
        .map((selectedChild) => ({
          id: uuidv4(),
          name: selectedChild.name,
          type: selectedChild.name,
          parentId: featureId,
          scheduleIds: [...currentFolder?.scheduleIds] || [],
          children: [],
        })),
    };

    const newclassOfServices = {
      id: uuidv4(),
      name: "Class of Services",
      type: type,
      parentId: parentId,
      COSid: "17254684354",
      children: [],
    };

    if (type === "services") {
      Promise.all([
        application.post("inboundCos/list/cos").then((res) => res.data),
        application.post("outboundCos/list/cos").then((res) => res.data),
      ])
        .then(([inboundRes, outboundRes]) => {
          const inboundNode = {
            id: uuidv4(),
            name: "Inbound Class of Services",
            type: "inbound services",
            parentId: newclassOfServices.id,
            COSid: "inbound_default_id",
            children: inboundRes.data.map((item) => ({
              id: uuidv4(),
              name: item.cos,
              type: "inbound",
              parentId: newclassOfServices.id,
              COSid: item.id,
              children: [],
            })),
          };

          const outboundNode = {
            id: uuidv4(),
            name: "Outbound Class of Services",
            type: "outbound services",
            parentId: newclassOfServices.id,
            COSid: "outbound_default_id",
            children: outboundRes.data.map((item) => ({
              id: uuidv4(),
              name: item.cos,
              type: "outbound",
              parentId: newclassOfServices.id,
              COSid: item.id,
              children: [],
            })),
          };

          newclassOfServices.children.push(inboundNode, outboundNode);

          setFolders((prevFolders) => {
            const newFolders = [...prevFolders];

            const addChild = (folders) => {
              for (let i = 0; i < folders.length; i++) {
                if (folders[i].id === parentId) {
                  folders[i].children.unshift(newclassOfServices);
                  return true;
                }
                if (folders[i].children) {
                  if (addChild(folders[i].children)) return true;
                }
              }
              return false;
            };

            addChild(newFolders);
            changeHospitalJSON(newFolders, "updated");
            return newFolders;
          });
        })
        .catch((err) => console.error("Failed to fetch COS data:", err));

      return; 
    }

    const addChildFolder = (folders) => {
      for (let i = 0; i < folders.length; i++) {
        if (folders[i].id === parentId) {
          if (type === "building" || type === "floor") {
            folders[i].children.push(newFolder);
          } else if (type === "features") {
            if (currentFolder?.type === "features") {
              currentFolder.children.push(...newFeature.children);
            } else {
              // check that parent has features if not then add it if yes then get that previous features and add the selected features to that
              const parentFolder = findFolderById(folders, parentId);
              const existingFeature = parentFolder.children.find(
                (child) => child.type === "features"
              );
              if (!existingFeature) {
                folders[i].children.unshift({ ...newFeature });
              } else {
                existingFeature.children.push(...newFeature.children);
              }
            }
          } else if (type === "services") {
            folders[i].children.unshift({ ...newclassOfServices });
          } else {
            folders[i].children.push({ ...newFolder, children: [] });
          }
          return true;
        }
        if (folders[i].children) {
          if (addChildFolder(folders[i].children)) return true;
        }
      }
      return false;
    };

    setFolders((prevFolders) => {
      const newFolders = [...prevFolders];
      addChildFolder(newFolders);
      return newFolders;
    });
  };

  const handleReloadCOS = async () => {
    try {
      const inboundData = await application
        .post("inboundCos/list/cos")
        .then((res) => res.data.data);

      const outboundData = await application
        .post("outboundCos/list/cos")
        .then((res) => res.data.data);

      setFolders((prevFolders) => {
        const updatedFolders = JSON.parse(JSON.stringify(prevFolders)); // Deep clone

        const syncCOSWithPerfectMatch = (cosList, cosType, targetNode) => {
          if (!targetNode) return;
          const apiKeysSet = new Set(
            cosList.map((cos) => `${cos.cos}_${String(cos.id)}`)
          );

          const beforeLength = targetNode.children.length;
          targetNode.children = targetNode.children.filter((child) => {
            const key = `${child.name}_${String(child.COSid)}`;
            const keep = apiKeysSet.has(key);
            if (!keep) console.log(`Removing -> ${key}`);
            return keep;
          });

          cosList.forEach((cos) => {
            const exists = targetNode.children.some(
              (child) =>
                String(child.name) === String(cos.cos) &&
                String(child.COSid) === String(cos.id)
            );
            if (!exists) {
              console.log(`Adding -> ${cos.cos}_${cos.id}`);
              targetNode.children.push({
                id: uuidv4(),
                name: cos.cos,
                type: cosType,
                parentId: targetNode.id,
                COSid: cos.id,
                children: [],
              });
            }
          });
        };

        const traverseAndSync = (nodes) => {
          nodes.forEach((node) => {
            if (node.type === "services" && node.children?.length > 0) {
              node.children.forEach((classOfService) => {
                if (
                  classOfService.type === "inbound services" ||
                  classOfService.type === "outbound services"
                ) {
                  const cosList =
                    classOfService.type === "inbound services"
                      ? inboundData
                      : outboundData;
                  syncCOSWithPerfectMatch(
                    cosList,
                    classOfService.type.includes("inbound")
                      ? "inbound"
                      : "outbound",
                    classOfService
                  );
                } else if (classOfService.children?.length) {
                  traverseAndSync(classOfService.children);
                }
              });
            }

            if (node.children?.length) traverseAndSync(node.children);
          });
        };

        traverseAndSync(updatedFolders);
        changeHospitalJSON(updatedFolders, "COS Smart Sync");
        return updatedFolders;
      });

      enqueueSnackbar("COS synced perfectly with API!", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } catch (error) {
      console.error("COS Sync Error:", error);
      enqueueSnackbar("Failed to sync COS", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

const handleReloadExtension = async () => {
  try {
    const response = await application.post("extensions/extensionType/list");
    const extensionData = response.data?.data || [];

    setFolders((prevFolders) => {
      const updatedFolders = JSON.parse(JSON.stringify(prevFolders)); // Deep clone to prevent mutation
      const companyNode = updatedFolders[0]; // Assuming company is always the first node

      let featuresNode = companyNode.children.find(
        (child) => child.type === "features"
      );
      if (!featuresNode) {
        featuresNode = {
          id: uuidv4(),
          name: "Features",
          type: "features",
          parentId: companyNode.id,
          children: [],
        };
        companyNode.children.unshift(featuresNode);
      }

      extensionData.forEach(({ ExtensionType, extensions }) => {
        const extensionChildren = extensions.map((ext) => ({
          id: uuidv4(),
          name: ext,
          type: ExtensionType,
          parentId: "", // Will set below
          children: [],
        }));

        if (ExtensionType === "acdgroup" || ExtensionType === "ivr") {
          // Add to Features -> CallCenter or IVR
          let featureChild = featuresNode.children.find(
            (child) =>
              child.type ===
              (ExtensionType === "acdgroup" ? "CallCenter" : "IVR")
          );
          if (!featureChild) {
            featureChild = {
              id: uuidv4(),
              name: ExtensionType === "acdgroup" ? "CallCenter" : "IVR",
              type: ExtensionType === "acdgroup" ? "CallCenter" : "IVR",
              parentId: featuresNode.id,
              children: [],
            };
            featuresNode.children.push(featureChild);
          }

          extensionChildren.forEach((child) => {
            child.parentId = featureChild.id;
            featureChild.children.push(child);
          });
        } else {
          // Group under ExtensionType node directly under company
          let extParentNode = companyNode.children.find(
            (child) => child.type === ExtensionType
          );
          if (!extParentNode) {
            extParentNode = {
              id: uuidv4(),
              name: ExtensionType,
              type: ExtensionType,
              parentId: companyNode.id,
              children: [],
            };
            companyNode.children.push(extParentNode);
          }

          extensionChildren.forEach((child) => {
            child.parentId = extParentNode.id;
            extParentNode.children.push(child);
          });
        }
      });

      changeHospitalJSON(updatedFolders, "Extension Sync");
      return updatedFolders;
    });

    enqueueSnackbar("Extensions synced successfully!", {
      variant: "success",
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  } catch (error) {
    console.error("Extension Sync Error:", error);
    enqueueSnackbar("Failed to sync Extensions", {
      variant: "error",
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  }
};


  const findFolderPath = (folders, targetId, currentPath = []) => {
    for (const folder of folders) {
      // Add the current folder to the path
      const newPath = [...currentPath, folder];

      // If the current folder matches the target ID, return its path
      if (folder.id === targetId) {
        return newPath;
      }

      // If the current folder has children, recursively search through them
      if (folder.children && folder.children.length > 0) {
        const foundPath = findFolderPath(folder.children, targetId, newPath);
        // If the folder is found in the children, return its path
        if (foundPath) {
          return foundPath;
        }
      }
    }

    // If the target folder is not found in the current level or its children, return null
    return null;
  };

  useEffect(() => {
    if (folders && currentFolder) {
      const path = findFolderPath(folders, currentFolder?.id);
      setFolderPath(path);
    }
  }, [currentFolder]);

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder);
  };

  const handleAddBuilding = () => {
    handleCloseMenu();
    // setIsOpen(true);
    handleFolderAdd(currentFolder.id, "building");
  };

  const handleAddFloor = () => {
    handleCloseMenu();
    handleFolderAdd(currentFolder.id, "floor");
  };

  const handleOpenAddForm = (e, type) => {
    setRoomInputDialog(type);
  };

const pageRender = () => {
  const commonProps = {
    currentFolder,
    folderPath,
    isDelete,
    handleFolderClick,
    getChildren,
    setAnchorEl,
    handleSelected,
    findFolderById,
    folders,
  };

  switch (currentPage) {
    case "Home":
      return <FileViewer {...commonProps} />;

    case "Calendar":
      return (
        <Schedule
          folders={folders}
          setFolders={setFolders}
          currentFolder={currentFolder}
          schedules={schedules}
          setSchedules={setSchedules}
        />
      );

    case "inboundCreate":
      return (
        <ClassOfService
          setCurrentFolder={setCurrentFolder}
          currentFolder={currentFolder}
          folders={folders}
          type={"inboundCreate"}
        />
      );

    case "outboundCreate":
      return (
        <ClassOfService
          setCurrentFolder={setCurrentFolder}
          currentFolder={currentFolder}
          folders={folders}
          type={"outboundCreate"}
        />
      );

    case "inbound":
      return (
        <ClassOfService
          setCurrentFolder={setCurrentFolder}
          currentFolder={currentFolder}
          folders={folders}
          type={"inbound"}
        />
      );

    case "outbound":
      return (
        <ClassOfService
          setCurrentFolder={setCurrentFolder}
          currentFolder={currentFolder}
          folders={folders}
          type={"outbound"}
        />
      );

    default:
      return <FileViewer {...commonProps} />;
  }
};


  const handleBackButton = () => {
    const backFolder = findFolderById(folders, currentFolder?.parentId);
    setCurrentFolder(backFolder);
    if (backFolder?.type === "services") {
      setCurrentPage(backFolder.type);
    } else {
      setCurrentPage("Home");
    }
  };

  // Search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFolders([]);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();

    // Helper function to check if a folder or any of its descendants match the search term
    const folderMatchesSearch = (folder) => {
      const nameMatches = folder.name?.toLowerCase().includes(searchTermLower);

      if (nameMatches) return true;

      if (folder.children && folder.children.length > 0) {
        return folder.children.some((child) => folderMatchesSearch(child));
      }

      return false;
    };

    // Helper function to create a filtered copy of the folder structure
    const filterFolderStructure = (folderList) => {
      return folderList
        .filter((folder) => folderMatchesSearch(folder))
        .map((folder) => ({
          ...folder,
          children:
            folder.children && folder.children.length > 0
              ? filterFolderStructure(folder.children)
              : [],
        }));
    };

    // Apply the filter to the folders
    const filtered = filterFolderStructure(folders);
    setFilteredFolders(filtered);
  }, [searchTerm, folders]);

  const deleteMultipleFolders = (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) return;

    setFolders((prevFolders) => {
      let newFolders = [...prevFolders];
      let deletedAny = false;

      const cosInboundIds = [];
      const cosOutboundIds = [];
      const extensionNames = [];

      [...selectedItems].reverse().forEach((folder) => {
        const folderId = folder?.id;
        const parentId = folder?.parentId;

        const deleteFolder = (foldersList) => {
          for (let i = 0; i < foldersList.length; i++) {
            if (foldersList[i].id === folderId) {
              if (foldersList[i].children?.length > 0) {
                enqueueSnackbar(
                  `You cannot delete ${foldersList[i]?.name} because it has children`,
                  {
                    variant: "error",
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                  }
                );
                return false;
              }

              // Classify deletion type
              if (foldersList[i].type === "inbound")
                cosInboundIds.push(foldersList[i]?.COSid);
              else if (foldersList[i].type === "outbound")
                cosOutboundIds.push(foldersList[i]?.COSid);
              else extensionNames.push(foldersList[i]?.name);

              foldersList.splice(i, 1);
              deletedAny = true;
              return true;
            }

            if (
              foldersList[i].children &&
              deleteFolder(foldersList[i].children)
            )
              return true;
          }
          return false;
        };

        deleteFolder(newFolders);
      });

      // Call Extension Delete API if needed
      if (extensionNames.length > 0) {
        deleteNode({ stringId: extensionNames });
      }

      // Call COS Delete APIs if needed
      if (cosInboundIds.length > 0) deleteInboundCos({ id: cosInboundIds });
      if (cosOutboundIds.length > 0) deleteOutboundCos({ id: cosOutboundIds });

      if (deletedAny) {
        changeHospitalJSON(newFolders, "deleted");
        enqueueSnackbar(`Successfully deleted selected items`, {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }

      setIsDelete(false);
      setSelectedItems([]);
      return newFolders;
    });
  };


  console.log(folders, "folders");
  
  return (
    <div style={{ display: "flex", height: "100vh", overflow: "auto" }}>
      <ResizableSidebar style={{ width: width }}>
        <Resizer onMouseDown={handleMouseDown}></Resizer>
        <FileNavigator>
          {/* Add search bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              marginBottom: "12px",
            }}
          />
          {/* Add a button to toggle multi-select mode */}
          {/* <MDButton
            onClick={() => {
              setIsMultiSelectMode(!isMultiSelectMode);
              if (!isMultiSelectMode) {
                setSelectedItems([]);
              }
            }}
            color={isMultiSelectMode ? "warning" : "info"}
            size="small"
          >
            {isMultiSelectMode ? "Cancel Selection" : "Select Multiple"}
          </MDButton> */}
          <List component="nav" aria-label="main mailbox folders">
            {(searchTerm.trim() ? filteredFolders : folders)
              ?.slice()
              .sort((a, b) => {
                // Handle null or undefined names
                const aName = a?.name || "";
                const bName = b?.name || "";
                const aIsNumeric = /^\d+$/.test(aName);
                const bIsNumeric = /^\d+$/.test(bName);

                // If one is alphabetic and one is numeric
                if (!aIsNumeric && bIsNumeric) return -1; // Alphabetic comes first
                if (aIsNumeric && !bIsNumeric) return 1; // Numeric comes after

                // If both are alphabetic or both are numeric, sort normally
                try {
                  return aName.localeCompare(bName, undefined, {
                    numeric: true,
                  });
                } catch (error) {
                  // Fallback to simple string comparison if localeCompare fails
                  return aName > bName ? 1 : -1;
                }
              })
              .map((folder) => (
                <Folder
                  key={folder.id}
                  folder={folder}
                  onFolderDelete={handleConfirmDelete}
                  onFolderEdit={handleFolderEditAndSave}
                  onFolderAdd={handleFolderAdd}
                  level={0}
                  currentFolder={currentFolder}
                  setCurrentFolder={setCurrentFolder}
                  handleSelected={handleSelected}
                  width={width}
                  darkMode={darkMode}
                  handleCurrentPage={handleCurrentPage}
                  featureListSelected={featureListSelected}
                  setFeatureListSelected={setFeatureListSelected}
                  folders={folders}
                  setFolders={setFolders}
                  featureList={featureList}
                  searchActive={searchTerm.trim().length > 0}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                  isMultiSelectMode={isMultiSelectMode}
                  deleteMultipleFolders={deleteMultipleFolders}
                  handleReloadCOS={handleReloadCOS}
                  handleReloadExtension={handleReloadExtension}
                />
              ))}
          </List>
        </FileNavigator>
      </ResizableSidebar>
      <FileViewerStyle>
        <MDBox
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
          }}
        >
          {(currentPage === "services" || currentPage === "Calendar") && (
            <>
              <Tooltip title="Back">
                <IconButton
                  onClick={handleBackButton}
                  sx={{ color: darkMode && "#fff" }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <MDTypography>
                {currentPage === "services" ? "Class of Services" : currentPage}
              </MDTypography>
            </>
          )}
        </MDBox>
        {pageRender()}
      </FileViewerStyle>
      {isDelete && (
        <AlertDelete
          name={currentFolder?.name}
          handleConfirmDelete={() => handleFolderDelete(currentFolder)}
          isDelete={isDelete}
          setIsDelete={setIsDelete}
          setCurrentFolder={setCurrentFolder}
        />
      )}
    </div>
  );
};
export default FileExplorer;
